import { createApi } from '@reduxjs/toolkit/query/react';
import type { StatisticsOptions } from './statisticsOptionsSlice';
import type { ApiResponse, GroupId, MetricUnit, Summary } from './types';
import { baseQueryWithAuth } from './utils';
import type {
  SpansCountData,
  TreesSummaryTotal,
} from '@/generated/types/navigator/types.gen';

export const statisticsApi = createApi({
  reducerPath: 'statisticsApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    filterLevel2Statistics: builder.query<
      ApiResponse<Level2Statistic, Level2Summary>,
      FilterLevel2StatisticsArgs
    >({
      query: ({ statisticsOptions }) => ({
        url: `/v1/statistics/level2`,
        method: 'POST',
        body: statisticsOptions,
      }),
    }),
    fetchSummaryWidgetsData: builder.query<StatisticNetworkSummary[], void>({
      query: () => ({
        url: '/v1/statistics/networks',
      }),
    }),
    spanCountForFilter: builder.query<
      SpansCountData,
      { filter: string; listId?: string }
    >({
      query: ({ filter, listId }) => ({
        url: `/v1/statistics/spans/count`,
        params: {
          filter_query: filter,
          ...(listId && { list_id: listId }),
        },
      }),
    }),
    fuelHazardStatistics: builder.query<FuelHazardStatistics, void>({
      query: () => ({
        url: `/v1/statistics/fuel_hazard/`,
      }),
    }),
    filterLevel2TreesStatistics: builder.query<
      ApiResponse<Level2StatisticTrees, Level2StatisticTreesSummary>,
      FilterLevel2TreesStatisticsArgs
    >({
      query: ({ treeStatisticsOptions }) => {
        const { page, limit } = treeStatisticsOptions;
        return {
          url: `/v1/statistics/trees/`,
          method: 'POST',
          body: {
            level1: treeStatisticsOptions.level1,
            network: treeStatisticsOptions.network,
            type: treeStatisticsOptions.type,
            selectedMetric: treeStatisticsOptions.selectedMetric,
            groups: treeStatisticsOptions.groups,
          },
          params: {
            limit,
            page,
          },
        };
      },
    }),
  }),
});

export const resetStatisticsApiState = statisticsApi.util.resetApiState;

interface FuelHazardStatistics {
  // The highest fuel_hazard_score across the client's active FDMA rows, or null
  // when the wildfire-intelligence flag is off or the client has no FDMA data.
  // Scored 0..1 in sync with the raw column — callers scale into the tiler's
  // uint16 source range when building rescale params.
  max: number | null;
}

interface HazardTreesStatisticsOptions extends QueryParams {
  network: number[];
  level1: string[];
  type: string;
  selectedMetric: string;
  groups?: Record<GroupId, string[] | undefined>;
}

export interface RiskDetails {
  rating: number;
  length: number;
  totalScore: number;
}

interface QueryParams {
  limit: number;
  page: number;
}
export interface Level2Statistic {
  level2: string;
  network: number;
  totalScore: number;
  length: number;
  metric: MetricUnit;
  breakdown: RiskDetails[];
}

export type TreeStat = {
  type: string;
  count: number;
  averagePerDistance: number;
  distanceUnit: string;
};

export interface Level2StatisticTrees {
  level2: string;
  stats: TreeStat[];
}

interface Level2StatisticTreesSummary {
  totals: TreesSummaryTotal[];
  totalTrees: number;
  totalResults: number;
}

interface Level2Summary extends Summary {
  networkScore: number;
}
interface VegetationSummary {
  distanceWithVegetation: number;
  distanceWithoutVegetation: number;
}

export interface StatisticNetworkSummary {
  network: number;
  totalAreaScanned: number;
  metric: MetricUnit;
  totalLevel1?: number;
  totalLevel2: number;
  vegetation?: VegetationSummary;
}

type FilterLevel2StatisticsArgs = {
  statisticsOptions: StatisticsOptions;
};

type FilterLevel2TreesStatisticsArgs = {
  treeStatisticsOptions: HazardTreesStatisticsOptions;
};

export const {
  useFilterLevel2StatisticsQuery,
  useFetchSummaryWidgetsDataQuery,
  useFilterLevel2TreesStatisticsQuery,
  useSpanCountForFilterQuery,
  useFuelHazardStatisticsQuery,
} = statisticsApi;
